if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const embroideryValidationError = this.validateEmbroideryInputs(formData);
        if (embroideryValidationError) {
          this.handleErrorMessage(embroideryValidationError);
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          this.querySelector('.loading__spinner').classList.add('hidden');
          return;
        }

        const giftPackingValidationError = this.validateGiftPackingInputs();
        if (giftPackingValidationError) {
          this.handleErrorMessage(giftPackingValidationError);
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.classList.remove('loading');
          this.querySelector('.loading__spinner').classList.add('hidden');
          return;
        }

        const shouldAddEmbroideryFee = this.shouldAddEmbroideryFee(formData);
        if (shouldAddEmbroideryFee) {
          config.headers['Content-Type'] = 'application/json';
          config.body = JSON.stringify(this.buildEmbroideryAddPayload(formData));
        } else {
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then(async (response) => {
            if (shouldAddEmbroideryFee) {
              this.normalizeEmbroideryAddResponse(response);
              await this.refreshCartIconBubble();
            }

            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.safeRenderCartContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.safeRenderCartContents(response);
              });
            }

          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      shouldAddEmbroideryFee(formData) {
        const personalizationValue = String(formData.get('properties[Personalization]') || '').trim().toLowerCase();
        const feeVariantId = String(formData.get('properties[_embroidery_fee_variant_id]') || '').trim();
        return personalizationValue === 'personalize' && /^\d+$/.test(feeVariantId);
      }

      validateEmbroideryInputs(formData) {
        const personalizationValue = String(formData.get('properties[Personalization]') || '').trim().toLowerCase();
        if (personalizationValue !== 'personalize') return null;

        const locationValue = String(formData.get('properties[Embroidery Location]') || '').trim();
        if (!locationValue) {
          return 'Please choose an embroidery location.';
        }

        const designValue = String(formData.get('properties[Embroidery Design]') || '').trim();
        if (!designValue) {
          return 'Please enter a name or initials for personalization.';
        }

        return null;
      }

      validateGiftPackingInputs() {
        const formId = this.form.getAttribute('id');
        const selector = formId
          ? `input[data-gift-packing-input][form="${formId}"]`
          : 'input[data-gift-packing-input]';
        const radios = document.querySelectorAll(selector);
        if (!radios.length) return null;

        const anyChecked = Array.from(radios).some((radio) => radio.checked);
        if (!anyChecked) {
          return 'Please choose whether to include the hospital packing list.';
        }

        return null;
      }

      buildEmbroideryAddPayload(formData) {
        const lineItem = this.formDataToLineItem(formData);
        const feeVariantId = Number(formData.get('properties[_embroidery_fee_variant_id]'));
        const feeToken = `embroidery-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

        lineItem.properties = lineItem.properties || {};
        lineItem.properties._embroidery_fee_token = feeToken;
        lineItem.properties._embroidery_fee_enabled = 'true';

        const feeItem = {
          id: feeVariantId,
          quantity: lineItem.quantity,
          properties: {
            _embroidery_fee_line: 'true',
            _embroidery_fee_token: feeToken,
          },
        };

        const payload = {
          items: [lineItem, feeItem],
        };

        if (this.cart) {
          payload.sections = this.cart.getSectionsToRender().map((section) => section.id);
          payload.sections_url = window.location.pathname;
          this.cart.setActiveElement(document.activeElement);
        }

        return payload;
      }

      normalizeEmbroideryAddResponse(response) {
        if (!response || !Array.isArray(response.items) || response.key) return;
        const nonFeeItem = response.items.find(
          (item) => String(item.properties?._embroidery_fee_line || '').trim().toLowerCase() !== 'true'
        );
        const mainItem = nonFeeItem || response.items[0];
        if (mainItem?.key) response.key = mainItem.key;
        if (mainItem?.id && !response.id) response.id = mainItem.id;
      }

      safeRenderCartContents(response) {
        if (!this.cart) return;
        try {
          this.cart.renderContents(response);
        } catch (error) {
          console.error(error);
        }
      }

      async refreshCartIconBubble() {
        const cartIcon = document.getElementById('cart-icon-bubble');
        if (!cartIcon) return;

        try {
          const response = await fetch(`${routes.cart_url}?section_id=cart-icon-bubble`);
          const html = await response.text();
          const parsed = new DOMParser().parseFromString(html, 'text/html');
          const source = parsed.getElementById('shopify-section-cart-icon-bubble') || parsed.querySelector('.shopify-section');
          if (source) {
            cartIcon.innerHTML = source.innerHTML;
          }
        } catch (error) {
          console.error(error);
        }
      }

      formDataToLineItem(formData) {
        const lineItem = {
          id: Number(formData.get('id')),
          quantity: Number(formData.get('quantity') || 1),
        };

        const sellingPlan = formData.get('selling_plan');
        if (sellingPlan) {
          lineItem.selling_plan = sellingPlan;
        }

        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.*)\]$/);
          if (!match) continue;
          if (value === '' || value == null) continue;
          properties[match[1]] = value;
        }
        if (Object.keys(properties).length) {
          lineItem.properties = properties;
        }

        return lineItem;
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
